/**
 * Skill：电商图片生图提示词专家（8+ 张：3 主图 1024² + 详情 1024×1536，出图映射 Seedream 最小像素）
 */

import { ECOM_PROMPT_EXPERT_SYSTEM } from '@/utils/ecomPromptExpertSystemPrompt'
import {
  getEcomDetailCopyLanguageBlock,
  getEcomDetailTypographyFallbackHint,
  isEcomDetailEnglishOutput,
  normalizeEcomDetailOutputLanguage
} from '@/utils/ecomDetailLanguage'
import {
  ECOM_SEEDREAM_SIZE_DETAIL,
  ECOM_SEEDREAM_SIZE_MAIN,
  normalizeEcomPromptPlanRow,
  resolveEcomSeedreamOutputSize
} from '@/utils/ecomSeedreamSizes'

export { ECOM_PROMPT_EXPERT_SYSTEM } from '@/utils/ecomPromptExpertSystemPrompt'

export const ECOM_SKILL_PROMPT_EXPERT_ID = 'ecom-skill-prompt-expert'

/** 默认 8 张出图计划（与 Skill 尺寸约定一致） */
export const ECOM_PROMPT_EXPERT_PLAN = [
  { index: 1, label: '主图1', size: ECOM_SEEDREAM_SIZE_MAIN, hint: '旗舰级主视觉' },
  { index: 2, label: '主图2', size: ECOM_SEEDREAM_SIZE_MAIN, hint: '消费者场景氛围' },
  { index: 3, label: '主图3', size: ECOM_SEEDREAM_SIZE_MAIN, hint: '利益点/组合版式' },
  { index: 4, label: '详情图1', size: ECOM_SEEDREAM_SIZE_DETAIL, hint: '首屏总述' },
  { index: 5, label: '详情图2', size: ECOM_SEEDREAM_SIZE_DETAIL, hint: '痛点场景' },
  { index: 6, label: '详情图3', size: ECOM_SEEDREAM_SIZE_DETAIL, hint: '解决机制' },
  { index: 7, label: '详情图4', size: ECOM_SEEDREAM_SIZE_DETAIL, hint: '信任证据' },
  { index: 8, label: '详情图5', size: ECOM_SEEDREAM_SIZE_DETAIL, hint: '收尾转化' }
]

const CN_NUM = {
  一: 1,
  二: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
  十: 10
}

function parseChineseIndex(raw) {
  const s = String(raw || '').trim()
  if (!s) return 0
  if (/^\d+$/.test(s)) return parseInt(s, 10)
  if (s.length === 1 && CN_NUM[s] != null) return CN_NUM[s]
  if (s.startsWith('十')) {
    const rest = s.slice(1)
    return 10 + (CN_NUM[rest] || 0)
  }
  if (s.endsWith('十')) {
    const head = s.slice(0, -1)
    return (CN_NUM[head] || 0) * 10
  }
  const ten = s.indexOf('十')
  if (ten > 0) {
    const a = CN_NUM[s.slice(0, ten)] || 0
    const b = CN_NUM[s.slice(ten + 1)] || 0
    return a * 10 + b
  }
  return 0
}

function extractSizeFromSection(body) {
  const m = String(body || '').match(/\[\s*(\d+)\s*x\s*(\d+)\s*\]/i)
  if (!m) return ''
  return `${m[1]}x${m[2]}`
}

function stripSectionHeader(body) {
  return String(body || '')
    .replace(/^[^\n]*\n?/, '')
    .replace(/\[\s*\d+\s*x\s*\d+\s*\]/gi, '')
    .trim()
}

function parseEcomPromptSectionIndex(headMatch, outputLanguage) {
  if (!headMatch) return 0
  const en = isEcomDetailEnglishOutput(outputLanguage)
  if (!en) {
    return parseChineseIndex(headMatch[1])
  }
  const fromCn = parseChineseIndex(headMatch[1])
  if (fromCn > 0) return fromCn
  const fromImage = parseInt(headMatch[2], 10)
  if (fromImage > 0) return fromImage
  const fromPlain = parseInt(headMatch[3], 10)
  if (fromPlain > 0) return fromPlain
  return 0
}

function ecomPromptExpertSectionHeadRegex(outputLanguage) {
  if (isEcomDetailEnglishOutput(outputLanguage)) {
    return /###\s*(?:第([一二三四五六七八九十百\d]+)张|Image\s*#?\s*(\d+)|(?:The\s+)?(\d+)(?:st|nd|rd|th)?\s*(?:image|panel))[^\n]*/i
  }
  return /###\s*第([一二三四五六七八九十百\d]+)张[^\n]*/
}

/**
 * 解析 DeepSeek 按 Skill 格式输出的多段提示词
 * @param {string} [outputLanguage] zh | en
 * @returns {{ index: number, label: string, size: string, prompt: string }[]}
 */
export function parseEcomPromptExpertSections(text, outputLanguage) {
  const raw = String(text || '').trim()
  if (!raw) return []
  const en = isEcomDetailEnglishOutput(outputLanguage)
  const splitRe = en
    ? /(?=###\s*(?:第|Image\s*#?\s*\d+|(?:The\s+)?\d+(?:st|nd|rd|th)?\s*(?:image|panel)))/i
    : /(?=###\s*第)/
  const headRe = ecomPromptExpertSectionHeadRegex(outputLanguage)
  const chunks = raw.split(splitRe).filter(c => headRe.test(c))
  const out = []
  for (const chunk of chunks) {
    if (/###\s*全局视觉锚点|###\s*Global\s+Visual\s+Anchor/i.test(chunk)) {
      const hasSection = en
        ? /###\s*(?:第[一二三四五六七八九十百\d]+张|Image\s*#?\s*\d+)/i.test(chunk)
        : /###\s*第[一二三四五六七八九十百\d]+张/.test(chunk)
      if (!hasSection) continue
    }
    const head = chunk.match(headRe)
    if (!head) continue
    const index = parseEcomPromptSectionIndex(head, outputLanguage)
    if (!index) continue
    const labelLine = head[0].replace(/^###\s*/, '').trim()
    const body = stripSectionHeader(chunk.replace(head[0], ''))
    const size =
      extractSizeFromSection(chunk) ||
      (index <= 3 ? ECOM_SEEDREAM_SIZE_MAIN : ECOM_SEEDREAM_SIZE_DETAIL)
    const prompt = body.trim()
    if (!prompt) continue
    out.push({ index, label: labelLine, size, prompt })
  }
  out.sort((a, b) => a.index - b.index)
  return out
}

export function isEcomPromptExpertSkill(skill) {
  if (!skill || typeof skill !== 'object') return false
  const id = String(skill.id || '').trim()
  if (id === ECOM_SKILL_PROMPT_EXPERT_ID) return true
  const title = String(skill.title || '').trim()
  return title.includes('电商图片生图提示词专家')
}

export function ecomPromptExpertSystemForSkill(skill, outputLanguage) {
  const custom = skill && skill.systemPrompt != null ? String(skill.systemPrompt).trim() : ''
  const base = custom || ECOM_PROMPT_EXPERT_SYSTEM
  const lang = normalizeEcomDetailOutputLanguage(outputLanguage)
  return `${base}\n\n${getEcomDetailCopyLanguageBlock(lang)}`
}

/**
 * @param {object} ctx — productName, sellingPoints, extraPrompt, upstreamText, platform
 */
export function buildEcomPromptExpertUserMessage(ctx) {
  const lang = normalizeEcomDetailOutputLanguage(ctx && ctx.outputLanguage)
  const en = isEcomDetailEnglishOutput(lang)
  const lines = en
    ? [
        'Follow this skill strictly: first output a ### Global Visual Anchor block (shared by all images), then at least 8 full image prompts as ### Image 1 … ### Image 8 (you may also use ### 第1张 … ### 第8张 headers).',
        'Mark the first 3 as main images with [1024x1024]; from image 4 onward use [1024x1536] for detail images.',
        'End each prompt with: strictly follow the global visual anchor; keep background color, lighting and mood identical across the set.',
        getEcomDetailCopyLanguageBlock(lang),
        'Product information:'
      ]
    : [
        '请严格按本技能输出：先输出 ### 全局视觉锚点声明（所有图片共享），再输出至少 8 张完整生图提示词（### 第一张 … ### 第八张）。',
        '前 3 张主图标注 [1024x1024]，第 4 张起详情图标注 [1024x1536]。',
        '每张提示词末尾须包含「严格遵循前述全局视觉锚点，与前后图保持背景色、光线、氛围完全一致。」',
        getEcomDetailCopyLanguageBlock(lang),
        '商品信息如下：'
      ]
  const name = String(ctx.productName || '').trim()
  const points = String(ctx.sellingPoints || '').trim()
  const extra = String(ctx.extraPrompt || '').trim()
  const up = String(ctx.upstreamText || '').trim()
  const imgInfo = String(ctx.upstreamImageInfo || '').trim()
  const plat = String(ctx.platform || 'generic').trim()
  if (imgInfo) lines.push(`【上游商品图视觉分析】\n${imgInfo}`)
  if (name) lines.push(`商品名称：${name}`)
  if (points) lines.push(`核心卖点：\n${points}`)
  if (extra) lines.push(`补充要求：${extra}`)
  if (up) lines.push(`上游参考文案：\n${up}`)
  if (plat) lines.push(`目标平台：${plat}`)
  if (!name && !points && !up && !imgInfo) {
    lines.push('（未填写文字时请结合参考图理解品类、外观与卖点）')
  }
  return lines.join('\n\n')
}

/**
 * 将解析结果对齐到 8 张计划；不足时用 plan.hint 作兜底描述
 */
export function alignEcomPromptExpertToPlan(parsed, ctx) {
  const byIndex = {}
  for (const row of parsed || []) {
    if (row && row.index > 0) byIndex[row.index] = row
  }
  const en = isEcomDetailEnglishOutput(ctx && ctx.outputLanguage)
  const name =
    String(ctx.productName || '').trim() || (en ? 'product' : '商品')
  const typoHint = getEcomDetailTypographyFallbackHint(ctx && ctx.outputLanguage)
  return ECOM_PROMPT_EXPERT_PLAN.map(plan => {
    const hit = byIndex[plan.index]
    const prompt =
      hit && hit.prompt
        ? hit.prompt
        : en
          ? [
              `E-commerce ${plan.label}, ${plan.hint}.`,
              `Product: ${name}.`,
              String(ctx.sellingPoints || '').trim(),
              `Flagship commercial photography, keep product appearance consistent with reference, ${typoHint}.`
            ]
          : [
              `电商${plan.label}，${plan.hint}。`,
              `商品：${name}。`,
              String(ctx.sellingPoints || '').trim(),
              `旗舰级商业摄影、严格保持商品外观与参考图一致、${typoHint}。`
            ]
            .filter(Boolean)
            .join(' ')
    const row = {
      index: plan.index,
      label: (hit && hit.label) || `第${plan.index}张（${plan.label}）`,
      size: resolveEcomSeedreamOutputSize((hit && hit.size) || plan.size, plan.index <= 3),
      prompt
    }
    return normalizeEcomPromptPlanRow(row)
  })
}

export function getEcomPromptExpertBuiltinSkill() {
  return {
    id: ECOM_SKILL_PROMPT_EXPERT_ID,
    title: '电商图片生图提示词专家',
    desc: '8+ 张：3 主图 1024² + 详情 1024×1536，全局视觉锚点强制统一背景/光影/风格',
    prompt:
      '资深电商视觉策划与AI生图提示词专家。根据商品信息和参考图，自动润色产品卖点，生成8张以上专业电商图片提示词（前3张主图1024x1024，后续详情图1024x1536），严格保持商品外观一致，输出旗舰级商业摄影视觉与模块化详情叙事，并强制所有图片背景色、光影、风格全局统一。',
    systemPrompt: ECOM_PROMPT_EXPERT_SYSTEM,
    image: '',
    batchMode: true
  }
}

/** 专家 Skill 置顶，再合并接口与其余内置 */
export function mergeEcomDetailSkillList(apiList, builtinList) {
  const expert = getEcomPromptExpertBuiltinSkill()
  // 暂时仅保留「电商图片生图提示词专家」
  return [expert]
  // const api = Array.isArray(apiList) ? apiList : []
  // const builtins = Array.isArray(builtinList) ? builtinList : []
  // const out = []
  // const seen = new Set()
  // out.push(expert)
  // seen.add(expert.id)
  // for (const s of api) {
  //   if (!s || !s.id) continue
  //   const id = String(s.id)
  //   if (seen.has(id)) continue
  //   seen.add(id)
  //   out.push(s)
  // }
  // for (const b of builtins) {
  //   if (!b || !b.id || seen.has(b.id)) continue
  //   seen.add(b.id)
  //   out.push(b)
  // }
  // return out
}
